import { memo, useCallback } from 'react';
import {
  Divider, Typography, Grid, InputAdornment,
  Stack, IconButton, Button as MuiButton,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import { DateRangePicker } from '@client/components/forms/DateRangePicker';
import { TextField } from '@client/components/forms/TextField';
import { GoogleSearch } from '@client/components/forms/GoogleSearch';
import { ProfileTypes } from '@sda/enums';
import { ToggleField } from '@client/components/forms/ToggleField';
import { useSelector } from '@client/hooks/redux.hooks';
import { useIsMobile } from '@client/hooks/dom.hooks';
import { Prompt } from '@client/components/globals/Modal/Prompt';
import { getSessionUserAdminStatus } from '@client/selectors/helpers/login';
import { ClearDateRange } from './ClearDateRange';
import { Filters } from './Filters';

const useStyles = makeStyles({ name: 'FilterModal' })((theme) => ({
  root: {
    [theme.breakpoints.up('md')]: {
      marginBottom: theme.spacing(2 * -1),
      marginTop: theme.spacing(3),
      minWidth: theme.breakpoints.values.wideModal - 80,
      width: theme.breakpoints.values.wideModal - 80,
    },
  },
  closeIcon: {
    padding: 0,
    minWidth: 26,
    minHeight: 26,
  },
  searchButton: {
    marginBottom: theme.spacing(3),
  },
}));

export const FilterModal = memo(() => {
  const { classes } = useStyles();
  const isMobile = useIsMobile();

  const isAdmin = useSelector(getSessionUserAdminStatus);

  const handleCloseButtonClick = useCallback(() => {
    Prompt.hide();
  }, []);

  const gridSpacing = isMobile ? 2 : 1;

  return (
    <Stack
      spacing={3}
      className={classes.root}
    >
      <div>
        <Grid
          container
          alignItems="center"
          spacing={gridSpacing}
        >
          {!isMobile ? (
            <Grid
              item
              xs={12}
              md={5}
            >
              <Typography
                component="label"
                htmlFor="sda-connect-filter-search-type"
                variant="subtitle2"
              >
                Search for
              </Typography>
            </Grid>
          ) : (
            <Grid
              alignItems="flex-start"
              justifyContent="space-between"
              spacing={1}
              wrap="nowrap"
              xs={12}
              item
              container
            >
              <Grid
                item
                zeroMinWidth
              >
                <Typography
                  component="label"
                  htmlFor="sda-connect-filter-search-type"
                  variant="subtitle2"
                >
                  Search for
                </Typography>
              </Grid>

              <Grid item>
                <IconButton
                  onClick={handleCloseButtonClick}
                  size="large"
                  className={classes.closeIcon}
                >
                  <CloseIcon />
                </IconButton>
              </Grid>
            </Grid>
          )}

          <Grid
            item
            xs={12}
            md={7}
          >
            <ToggleField
              id="sda-connect-filter-search-type"
              name="searchType"
              states={[
                { label: 'Artists', value: ProfileTypes.ACT, color: 'primary' },
                { label: 'Hosts', value: ProfileTypes.VENUE, color: 'warning' },
              ]}
            />
          </Grid>
        </Grid>
      </div>

      {Boolean(isAdmin) && (
        <>
          <div>
            <Divider variant="extended" />
          </div>

          <div>
            <Grid
              container
              alignItems="center"
              spacing={gridSpacing}
            >
              <Grid
                item
                xs={12}
                md={5}
              >
                <Typography
                  component="label"
                  htmlFor="sda-connect-filter-keyword"
                  variant="subtitle2"
                >
                  Keyword
                </Typography>
              </Grid>

              <Grid
                item
                xs={12}
                md={7}
              >
                <TextField
                  id="sda-connect-filter-keyword"
                  name="query"
                  variant="outlined"
                  fullWidth
                />
              </Grid>
            </Grid>
          </div>
        </>
      )}

      <div>
        <Divider variant="extended" />
      </div>

      <div>
        <Grid
          container
          alignItems="center"
          spacing={gridSpacing}
        >
          <Grid
            item
            xs={12}
            md={5}
          >
            <Typography
              component="label"
              htmlFor="sda-connect-filter-city"
              variant="subtitle2"
            >
              Location
            </Typography>
          </Grid>

          <Grid
            item
            xs={12}
            md={7}
          >
            <GoogleSearch
              id="sda-connect-filter-city"
              name="compiledAddress"
              type="(cities)"
              variant="outlined"
              fullWidth
              includeLatLngField
              required
              loadGoogleScriptExternally
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
        </Grid>
      </div>

      <div>
        <Divider variant="extended" />
      </div>

      <div>
        <Grid
          container
          alignItems="flex-start"
          spacing={gridSpacing}
        >
          <Grid
            item
            xs={12}
            md={5}
          >
            <Typography
              component="label"
              htmlFor="sda-connect-filter-date"
              variant="subtitle2"
              gutterBottom
            >
              Dates
            </Typography>

            <ClearDateRange />
          </Grid>

          <Grid
            item
            xs={12}
            md={7}
          >
            <DateRangePicker
              id="sda-connect-filter-date"
              name="dateRange"
              disablePast
            />
          </Grid>
        </Grid>
      </div>

      <div>
        <Divider variant="extended" />
      </div>

      <Filters
        gridSpacing={gridSpacing}
        isMobile={isMobile}
      />

      <div>
        <MuiButton
          type="submit"
          size="large"
          variant="contained"
          className={classes.searchButton}
          fullWidth
        >
          Search
        </MuiButton>
      </div>
    </Stack>
  );
});
